import { InputField, ImageUploadField, TextareaField } from "../FormFields";
import PageLayout from "../PageFromLayout";
import { useState, useEffect, useRef } from "react";
import Alert from "components/common/Alert";
import { getImageUrl } from "utils/imageHelper";
import { validateForm } from "utils/validation";
import FormSection from "../FormSection";
import { FiFileText, FiEye, FiExternalLink, FiTrash2 } from "react-icons/fi";
import Modal from "components/common/Modal";
import { validateImageFile } from "utils/common";
export default function SpeakerForm({
  initialData,
  onSubmit,
  onBack,
  alert,
  setAlert,
}) {
  const [selectedOrganizer, setSelectedOrganizer] = useState(
    localStorage.getItem("company_id") || ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [existingPdfUrl, setExistingPdfUrl] = useState(null);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfRemoved, setPdfRemoved] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    profession: "",
    website: "",
    description: "",
    facebookLink: "",
    xLink: "",
    linkedinLink: "",
    whatsappNumber: "",
    profileImage: null,
    speakerDoc: null,
  });

  const isEdit = Boolean(initialData);

  // Validation Schema
  const schema = {
    name: {
      required: true,
      label: "Full Name",
      minLength: 2,
    },
    email: {
      required: false,
      type: "email",
      label: "E-mail",
    },
    profession: {
      required: true,
      label: "Profession",
    },
    website: {
      required: false,
      type: "url",
      label: "Website",
    },
    facebookLink: {
      required: false,
      type: "url",
      label: "Facebook Link",
    },
    xLink: {
      required: false,
      type: "url",
      label: "X (Twitter) Link",
    },
    linkedinLink: {
      required: false,
      type: "url",
      label: "LinkedIn Link",
    },
    whatsappNumber: {
      required: false,
      type: "phone",
      label: "WhatsApp Number",
    },
    profileImage: {
      required: false,
      type: "image",
      label: "Profile Image",
      imageOptions: {
        maxSizeMB: 2,
        allowedTypes: ["image/jpeg", "image/png"],
      },
    },
  };

  const [errors, setErrors] = useState({});
  const [existingImage, setExistingImage] = useState(null);
  const alertRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((p) => ({
      ...p,
      [name]: "",
    }));
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    const name = e.target.name;

    if (!file) return;
    const { isValid, error } = await validateImageFile(file, 2);

    if (!isValid) {
      setErrors((prev) => ({ ...prev, [name]: error }));
      return; // Stop execution if validation fails
    }

    // 2. Clear error and update state if valid
    setErrors((prev) => ({ ...prev, [name]: "" }));

    setFormData((p) => ({
      ...p,
      [name]: file,
    }));

    setErrors((p) => ({
      ...p,
      [name]: "",
    }));
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const userId = userData?.userId || userData?._id || "";

    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        facebookLink: initialData.facebookLink || "",
        xLink: initialData.xLink || "",
        linkedinLink: initialData.linkedinLink || "",
        whatsappNumber: initialData.whatsappNumber || "",
        company_id: selectedOrganizer || initialData.company_id || "",
        created_by: initialData.created_by || userId,
        updated_by: userId,
      }));
      setExistingImage(
        initialData.profileImage ? getImageUrl(initialData.profileImage) : null
      );
      setExistingPdfUrl(
        initialData.speakerDoc ? getImageUrl(initialData.speakerDoc) : null
      );
    } else {
      setFormData((prev) => ({
        ...prev,
        company_id: selectedOrganizer || "",
        created_by: userId,
        updated_by: userId,
      }));
    }
  }, [initialData, selectedOrganizer]);

  useEffect(() => {
    const handleOrganizerChange = (e) => {
      setSelectedOrganizer(e.detail);
    };

    window.addEventListener("companyChanged", handleOrganizerChange);
    return () =>
      window.removeEventListener("companyChanged", handleOrganizerChange);
  }, []);

  useEffect(() => {
    if (alert && alertRef.current) {
      alertRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [alert]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    // Validate form - checks optional fields ONLY IF they contain values
    const validationErrors = await validateForm(formData, schema);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      if (alertRef.current) {
        alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }

    setSubmitting(true);

    // Prepare FormData payload for file upload
    const uploadData = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "profileImage") {
        if (formData.profileImage instanceof File) {
          uploadData.append("profileImage", formData.profileImage);
        }
      } else if (key === "speakerDoc") {
        if (formData.speakerDoc instanceof File) {
          uploadData.append("speakerDoc", formData.speakerDoc);
        } else if (pdfRemoved) {
          // Existing document removed - send empty value to clear it
          uploadData.append("speakerDoc", "");
        }
      } else if (formData[key] !== null && formData[key] !== undefined) {
        uploadData.append(key, formData[key]);
      }
    });

    if (onSubmit) {
      await onSubmit(uploadData);
    }
    setSubmitting(false);
  };
  const handlePdfUpload = (e) => {
    const file = e.target.files?.[0];
    const name = e.target.name;

    if (!file) return;

    // Reject non-PDF files
    if (file.type !== "application/pdf") {
      setErrors((p) => ({
        ...p,
        [name]: "Only PDF documents (.pdf) are allowed",
      }));
      return;
    }

    // // Limit size (e.g. 5MB)
    // if (file.size > 5 * 1024 * 1024) {
    //   setErrors((p) => ({
    //     ...p,
    //     [name]: "File size exceeds maximum limit of 5MB",
    //   }));
    //   return;
    // }

    setFormData((p) => ({ ...p, [name]: file }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleRemovePdf = () => {
    if (existingPdfUrl) {
      setPdfRemoved(true);
    }
    setExistingPdfUrl(null);
    setFormData((p) => ({ ...p, speakerDoc: null }));
    setErrors((p) => ({ ...p, speakerDoc: "" }));
    setIsPdfModalOpen(false);
  };

  const getActivePdfSource = () => {
    if (formData.speakerDoc instanceof File) {
      return URL.createObjectURL(formData.speakerDoc);
    }
    return existingPdfUrl;
  };

  const activePdf = getActivePdfSource();
  return (
    <PageLayout
      onBack={onBack}
      onSubmit={handleSubmitForm}
      submitText={isEdit ? "Update Speaker" : "Create Speaker"}
      loading={submitting}
    >
      <form onSubmit={handleSubmitForm} className="flex flex-col gap-6">
        {alert && (
          <div ref={alertRef} className="scroll-mt-[100px] px-6 pt-3">
            <Alert type="error" message={alert} onClose={() => setAlert("")} />
          </div>
        )}

        {/* BASIC INFORMATION */}
        <FormSection title="Speaker Information">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <InputField
              label="Full Name"
              name="name"
              value={formData.name || ""}
              onChange={handleChange}
              placeholder="Enter full name"
              error={errors.name}
              required
            />

            <InputField
              label="Email"
              name="email"
              type="email"
              value={formData.email || ""}
              onChange={handleChange}
              placeholder="Enter email address"
              error={errors.email}
            />

            <InputField
              label="Profession"
              name="profession"
              value={formData.profession || ""}
              onChange={handleChange}
              placeholder="e.g. Senior Software Architect"
              error={errors.profession}
              required
            />

            <InputField
              label="Website"
              name="website"
              value={formData.website || ""}
              onChange={handleChange}
              placeholder="https://example.com"
              error={errors.website}
            />

            <div className="md:col-span-2">
              <TextareaField
                label="Description"
                name="description"
                value={formData.description || ""}
                onChange={handleChange}
                placeholder="Enter speaker description..."
                rows={3}
                error={errors.description}
              />
            </div>
          </div>
        </FormSection>

        {/* SOCIAL & CONTACT LINKS */}
        <FormSection title="Social & Contact Links (Optional)">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <InputField
              label="Facebook Profile URL"
              name="facebookLink"
              value={formData.facebookLink || ""}
              onChange={handleChange}
              placeholder="https://facebook.com/username"
              error={errors.facebookLink}
            />

            <InputField
              label="X (Twitter) Profile URL"
              name="xLink"
              value={formData.xLink || ""}
              onChange={handleChange}
              placeholder="https://x.com/username"
              error={errors.xLink}
            />

            <InputField
              label="LinkedIn Profile URL"
              name="linkedinLink"
              value={formData.linkedinLink || ""}
              onChange={handleChange}
              placeholder="https://linkedin.com/in/username"
              error={errors.linkedinLink}
            />

            <InputField
              label="WhatsApp Number"
              name="whatsappNumber"
              type="phone"
              value={formData.whatsappNumber || ""}
              onChange={handleChange}
              placeholder="e.g. 919876543210"
              error={errors.whatsappNumber}
            />
          </div>
        </FormSection>

        {/* MEDIA */}
        <FormSection title="Speaker Media">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <ImageUploadField
              label="Profile Image"
              recommended={
                <>
                  Recommended resolution: <b>300 × 300 px</b>. Accepted formats:
                  JPEG, PNG.
                </>
              }
              preview={
                formData.profileImage instanceof File
                  ? URL.createObjectURL(formData.profileImage)
                  : existingImage || ""
              }
              onUpload={handleFile}
              error={errors.profileImage}
              name="profileImage"
            />
            <div>
              <ImageUploadField
                label="PDF Document"
                // preview={
                //   formData.profileImage instanceof File
                //     ? URL.createObjectURL(formData.profileImage)
                //     : existingImage || ""
                // }
                onUpload={handlePdfUpload}
                error={errors.speakerDoc}
                name="speakerDoc"
                accept="application/pdf"
              />
              {activePdf && (
                <div className="mt-2 flex flex-col gap-3 rounded-lg border bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100 text-red-600">
                      <FiFileText className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-800">
                        {formData.speakerDoc instanceof File
                          ? formData.speakerDoc.name
                          : "Uploaded Speaker Document.pdf"}
                      </p>
                      <p className="text-[11px] text-gray-400">PDF Document</p>
                    </div>
                  </div>

                  {/* VIEW OPTIONS BUTTONS */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setIsPdfModalOpen(true)}
                      className="shadow-xs flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      <FiEye /> Modal View
                    </button>

                    <button
                      type="button"
                      onClick={handleRemovePdf}
                      className="shadow-xs flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                    >
                      <FiTrash2 /> Remove
                    </button>

                    {/* <button
                      type="button"
                      onClick={() =>
                        onViewPdfNewTab
                          ? onViewPdfNewTab(activePdf)
                          : window.open(activePdf, "_blank")
                      }
                      className="bg-sky-600 shadow-xs hover:bg-sky-700 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white"
                    >
                      <FiExternalLink /> Open Page
                    </button> */}
                  </div>
                </div>
              )}
            </div>
          </div>
        </FormSection>
      </form>
      <Modal
        open={isPdfModalOpen}
        onClose={() => setIsPdfModalOpen(false)}
        title="Speaker Document Preview"
        maxWidth="max-w-5xl" // Expanded width for comfortable PDF reading
      >
        <div className="h-[75vh] w-full overflow-hidden rounded-xl bg-gray-100">
          <iframe
            src={activePdf}
            title="PDF Document Viewer"
            className="h-full w-full border-none"
          />
        </div>
      </Modal>
    </PageLayout>
  );
}
